import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Printer, Save, ShieldCheck, MessageSquare } from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import '../styles/ScamSmart.css';

const STORAGE_KEY = 'ss_safe_word';

const ScamSmartSafeWord = () => {
  const [word, setWord] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v) { setWord(v); setSaved(true); }
    } catch {}
  }, []);

  const save = (e) => {
    e?.preventDefault?.();
    if (!word.trim()) return;
    try { localStorage.setItem(STORAGE_KEY, word.trim()); } catch {}
    setSaved(true);
  };

  const handlePrint = () => {
    // Persist before opening the print dialog
    if (word.trim()) {
      try { localStorage.setItem(STORAGE_KEY, word.trim()); } catch {}
      setSaved(true);
    }
    setTimeout(() => window.print(), 100);
  };

  return (
    <div className="ss-page" data-testid="ss-safeword-page">
      <header className="ss-header ss-no-print">
        <div className="ss-header-inner">
          <Link to="/scamsmart/dashboard" className="ss-brand">
            <img src="https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png" alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">Family Safe Word</div>
            </div>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ScamIQBadge />
            <Link to="/scamsmart/dashboard" className="ss-header-cta">Dashboard</Link>
          </div>
        </div>
      </header>

      <div className="ss-test-wrap">
        <div className="ss-test-container">
          <Link to="/scamsmart/dashboard" className="ss-back ss-no-print"><ArrowLeft size={14} /> Dashboard</Link>

          <div className="ss-section-eyebrow ss-no-print">Family Safe Word</div>
          <h1 className="ss-section-title ss-no-print" style={{ fontSize: 28 }}>
            One word. A whole family of fewer scams.
          </h1>
          <p className="ss-section-lede ss-no-print" style={{ fontSize: 15 }}>
            A family safe word is a quiet, private check between you and the people you love. Use it when
            anything feels urgent, emotional, or unfamiliar — and especially when someone messages from a new
            number, claims to be family, or asks for money, codes, or gift cards.
          </p>

          <div className="ss-safeword-card ss-no-print">
            <form onSubmit={save}>
              <label style={{ fontSize: 13, color: '#5C6B73', fontWeight: 600 }}>
                Choose your family safe word or phrase
              </label>
              <input
                type="text"
                className="ss-email-input"
                placeholder="e.g. blue parrot, or whatever your family will remember"
                value={word}
                onChange={(e) => { setWord(e.target.value); setSaved(false); }}
                style={{ marginTop: 8 }}
                data-testid="ss-safeword-input"
              />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="submit" className="ss-btn-primary" disabled={!word.trim()} data-testid="ss-safeword-save">
                  <Save size={16} /> {saved ? 'Saved' : 'Save safe word'}
                </button>
                <button type="button" className="ss-btn-ghost" onClick={handlePrint} disabled={!word.trim()} data-testid="ss-safeword-print">
                  <Printer size={16} /> Print family card
                </button>
              </div>
              {saved && <p style={{ fontSize: 12, color: '#1d8a4f', marginTop: 10 }}>✓ Saved on this device. Print the card and stick it on the fridge.</p>}
            </form>
          </div>

          {/* === Printable family card === */}
          {word.trim() && (
            <div className="ss-safeword-print" data-testid="ss-safeword-card">
              <h3><ShieldCheck size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Our family scam rule</h3>
              <p>
                If a message or call feels urgent, emotional, or asks for money, codes, or gift cards — <strong>we pause and verify.</strong>
              </p>
              <span className="ss-safeword-the-word" data-testid="ss-safeword-display">{word.trim()}</span>
              <p>
                <strong>When to ask for the safe word:</strong> someone says they're family from a new number; an urgent money request; an unexpected request to send a code or buy gift cards; a stranger saying "don't tell anyone".
              </p>
              <p>
                <strong>If they cannot confirm the word:</strong> hang up or stop replying, and verify through a known number or trusted family channel.
              </p>
              <p style={{ marginTop: 18, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                ScamSmart by All Secured &middot; allsecured.com.au
              </p>
            </div>
          )}

          <div className="ss-trusted ss-no-print" style={{ marginTop: 28 }}>
            <div className="ss-trusted-title"><MessageSquare size={12} style={{ verticalAlign: '-2px', marginRight: 6 }} /> Practise scenario</div>
            <strong>Imagine this SMS arrives tonight:</strong>
            <p style={{ fontStyle: 'italic', color: '#0A1F44', margin: '8px 0 12px' }}>
              "Hi Mum, I dropped my phone and this is my temp number. I really need help with $850 today. Please don't tell Dad."
            </p>
            Pick up the phone. Call the saved number first. If that doesn't answer, message the safe word here.
            If they can answer with <strong>"{word.trim() || 'your safe word'}"</strong>, it's really them. If not — trust resets.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamSmartSafeWord;
